import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > window.innerHeight - 64);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Packages', href: '/packages' },
    { name: 'Equipment', href: '/products' },
    { name: 'My Booking', href: '/my-booking' },
    { name: 'Contact', href: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  const showIconOnly = isHomePage && !scrolled;

  return (
    <header className={`fixed w-full top-0 z-50 backdrop-blur-xl border-b border-slate-700 shadow-sm transition-all duration-300 ${
      isHomePage && !scrolled ? 'bg-slate-900/95' : 'bg-slate-900'
    }`}>
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center items-center h-20 md:h-24 relative">
          {/* Logo - always full lockup */}
          <Link to="/" className="absolute left-0 flex items-center group -my-2">
            <img 
              src="/AudioApex_lockup_transparent.svg"
              alt="Audio Apex"
              className="h-10 md:h-14 w-auto object-contain transform group-hover:scale-105 transition-transform duration-300"
            />
          </Link>

          {/* Desktop Navigation - Centered */}
          <div className="hidden md:flex items-center gap-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-base font-medium transition-colors duration-200 relative group ${
                  isActive(item.href)
                    ? 'text-white font-semibold'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {item.name}
                <span
                  className={`absolute -bottom-1 left-0 w-full h-0.5 bg-white transform origin-left transition-transform duration-200 ${
                    isActive(item.href) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}
                />
              </Link>
            ))}
          </div>

          {/* CTA Button - Desktop */}
          <div className="hidden md:block absolute right-0">
            <Link to="/contact">
              <Button className="bg-white hover:bg-gray-100 text-slate-900 font-medium transition-all duration-300 shadow-lg text-sm px-4 py-2 h-auto">
                <Phone className="w-4 h-4 mr-2" />
                Get in Touch
              </Button>
            </Link>
          </div>

          {/* CTA Button - Mobile */}
          <div className="md:hidden absolute right-0 flex items-center gap-2">
            <Link to="/contact">
              <Button size="sm" className="bg-white hover:bg-gray-100 text-slate-900 font-medium transition-all duration-300 shadow-lg px-2.5 py-1 h-auto text-[11px]">
                <Phone className="w-3 h-3 mr-1" />
                Get in Touch
              </Button>
            </Link>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-slate-800 transition-colors duration-200"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <Menu className="w-6 h-6 text-white" />
            )}
          </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-700 animate-in slide-in-from-top duration-200">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`block py-3 px-4 text-base font-medium rounded-lg transition-colors duration-200 ${
                  isActive(item.href)
                    ? 'text-white bg-slate-800'
                    : 'text-gray-300 hover:bg-slate-800 hover:text-white'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            <Link to="/contact" className="block mt-4" onClick={() => setMobileMenuOpen(false)}>
              <Button className="w-full bg-white hover:bg-gray-100 text-slate-900 font-medium">
                <Phone className="w-4 h-4 mr-2" />
                Get a Quote
              </Button>
            </Link>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
